/* eslint-disable no-console -- error handling*/
import { type Association, type Group, Opportunity } from "@entities";
import { opportunityParticipantRepo, opportunityRepo } from "@repositories";
import { linko } from "@shared/linko";
import { Roles } from "@shared/Roles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { remult } from "remult";
import { useAlert } from "../contexts/AlertProvider";
import { useOrganization } from "../contexts/OrganizationProvider";
import { useUser } from "../contexts/UserProvider";
import usePromptMatchWeight, {
  MatchWeightPromptCancelled,
  resolveMatchWeightForAdd,
} from "../dialog-components/MatchWeightDialog";
import { useCommunities } from "./communities.query";
import { ADMIN_LIST_QUERY_OPTIONS, adminParticipantActionErrorMessage, runInChunks } from "../helpers/admin-list-query";
import { invalidateEventParticipantQueries } from "./invalidate-opportunities-table";

export function useEvents(
  options?: {
    openOnly?: boolean;
    happeningNow?: boolean;
    history?: boolean;
    teams?: boolean;
    thisWeek?: boolean;
    all?: boolean;
    pendingRequests?: boolean;
    allWithoutTimes?: boolean;
    minDate?: Date;
    isCalendar?: boolean;
    startDate?: Date;
    endDate?: Date;
  },
  selectedTeams?: string[],
) {
  const {
    openOnly = false,
    happeningNow = false,
    history = false,
    teams = false,
    all = false,
    pendingRequests = false,
    allWithoutTimes = false,
    minDate,
    isCalendar = false,
    startDate,
    endDate,
  } = options ?? {};
  const { communities } = useCommunities();
  const { currOrganization } = useOrganization();

  const { data, ...query } = useQuery({
    queryKey: [
      "events",
      currOrganization?.id,
      openOnly,
      happeningNow,
      history,
      teams,
      all,
      selectedTeams,
      allWithoutTimes,
      pendingRequests,
      minDate,
      isCalendar,
      startDate?.getTime(),
      endDate?.getTime(),
    ],
    queryFn: async () => {
      const now = new Date();
      const startOfMinDate = new Date(minDate || "");
      startOfMinDate.setHours(0, 0, 0, 0);

      const endOfMinDate = new Date(minDate || "");
      endOfMinDate.setHours(23, 59, 59, 999);
      const events = await opportunityRepo.find({
        where: {
          status: pendingRequests ? "AID_REQUEST" : { $ne: "AID_REQUEST" },
          $and: [
            { organization: { $id: currOrganization!.id } },
            openOnly ? Opportunity.isInNeedOfParticipants({}) : {}, // Filter for open only events.
            ...(happeningNow // Filter for live events.
              ? [
                {
                  $or: [{ startDate: { $lte: now }, endDate: { $gt: now } }],
                },
                { isClosed: false },
              ]
              : []),
            ...(openOnly // Filter for all events.
              ? [
                {
                  $or: [{ endDate: { $gt: now } }],
                },
                { isClosed: false },
              ]
              : []),
            ...(history
              ? [{ endDate: { $lt: now } }]
              : !isCalendar &&
                  !happeningNow &&
                  !all &&
                  !teams &&
                  !pendingRequests &&
                  !openOnly
                ? [{ startDate: { $gt: now } }]
                : []),
            ...((selectedTeams?.length || 0) > 0
              ? [
                Opportunity.isInOneOfTheseTeams({
                  teams: selectedTeams || [],
                }),
              ]
              : []),
            ...(isCalendar && startDate && endDate
              ? [
                {
                  $and: [
                    { startDate: { $lte: endDate } },
                    { endDate: { $gte: startDate } },
                  ],
                },
              ]
              : []),
            ...((minDate && [
              {
                $or: [
                  {
                    $and: [
                      {
                        createdAt: {
                          $lte: minDate,
                        },
                      },
                    ],
                  },
                  {
                    $and: [
                      {
                        endDate: {
                          $gte: minDate,
                        },
                      },
                      {
                        createdAt: {
                          $lte: minDate,
                        },
                      },
                    ],
                  },
                  {
                    $and: [
                      {
                        endDate: {
                          $gte: minDate,
                        },
                      },
                      {
                        startDate: {
                          $gte: startOfMinDate,
                          $lt: endOfMinDate,
                        },
                      },
                    ],
                  },
                ],
              },
            ]) ??
              []),
          ],
        },
        include: {
          progressStatus: true,
          teams: true,
          partnerOrganization: true,
          associationRequested: {
            include: {
              user: true,
            },
          },
        },
        limit: isCalendar ? 2000 : 400,
        orderBy: { startDate: history ? "desc" : "asc" },
      });

      return { events };
    },
    refetchInterval: 1000 * 60 * 3,
    ...ADMIN_LIST_QUERY_OPTIONS,
    enabled:
      remult.isAllowed(Roles.AdminsAndManager) &&
      !!currOrganization?.id &&
      !!communities,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return { data, ...query };
}

export function useAllEvents() {
  return useEvents({
    all: true,
    allWithoutTimes: true,
  });
}

export function useAddUserToOpportunity() {
  const { setAlertText, setSeverity } = useAlert();
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const promptMatchWeight = usePromptMatchWeight();
  const { data: addedUser, ...mutation } = useMutation({
    mutationKey: ["addUserToOpportunity", currentUser?.id],
    mutationFn: async ({
      opportunityId,
      associationId,
      showSuccessAlert = true,
      defaultMatchWeight,
      matchWeight,
    }: {
      opportunityId: string;
      associationId: string;
      showSuccessAlert?: boolean;
      defaultMatchWeight?: number;
      matchWeight?: number;
    }) => {
      if (!associationId || !opportunityId) return;

      const resolvedMatchWeight = await resolveMatchWeightForAdd({
        opportunityId,
        defaultMatchWeight,
        matchWeight,
        prompt: promptMatchWeight,
      });

      const { data: res } =
        await linko.opportunityParticipant.mutations.addVolunteerToOpportunityAndNotify(
          {
            opportunityId,
            associationId,
            ...(resolvedMatchWeight != null ? { matchWeight: resolvedMatchWeight } : {}),
          },
        );

      if (res?.alreadyApproved) {
        if (showSuccessAlert) {
          setAlertText("המשתתף/ת כבר רשום/ה למשימה");
          setSeverity("warning");
        }
        return res.participant;
      }

      if (showSuccessAlert) {
        setAlertText("המשתתפים נוספו בהצלחה");
        setSeverity("success");
      }

      return res?.participant;
    },
    onSuccess: (_data, variables) => {
      void invalidateEventParticipantQueries(
        queryClient,
        variables.opportunityId,
      );
    },
    onError: (error) => {
      if (error instanceof MatchWeightPromptCancelled) return;
      console.error(error.message);
      setAlertText(adminParticipantActionErrorMessage(error));
      setSeverity("error");
    },
  });

  return {
    addedUser,
    ...mutation,
  };
}

export function useAddGroupToOpportunity() {
  const { setAlertText, setSeverity } = useAlert();
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const promptMatchWeight = usePromptMatchWeight();

  const { ...mutation } = useMutation({
    mutationKey: ["addGroupToOpportunity", currentUser?.id],
    mutationFn: async ({
      opportunityId,
      groupIds,
      showSuccessAlert = true,
      defaultMatchWeight,
      matchWeight,
    }: {
      opportunityId: string;
      groupIds: string[];
      showSuccessAlert?: boolean;
      defaultMatchWeight?: number;
      matchWeight?: number;
    }) => {
      if (!opportunityId || groupIds.length === 0) return;

      const resolvedMatchWeight = await resolveMatchWeightForAdd({
        opportunityId,
        defaultMatchWeight,
        matchWeight,
        prompt: promptMatchWeight,
      });

      let addedCount = 0;
      let alreadyApprovedCount = 0;

      await runInChunks(groupIds, 4, async (groupId) => {
        const { data: res } =
          await linko.opportunityParticipant.mutations.addGroupToOpportunityAndNotify(
            {
              opportunityId,
              groupId,
              ...(resolvedMatchWeight != null
                ? { matchWeight: resolvedMatchWeight }
                : {}),
            },
          );

        if (res?.alreadyApproved) {
          alreadyApprovedCount += 1;
          return;
        }

        addedCount += 1;
      });

      if (!showSuccessAlert) return;

      if (addedCount === 0 && alreadyApprovedCount > 0) {
        setAlertText(
          alreadyApprovedCount === 1
            ? "הקבוצה כבר רשומה למשימה"
            : "כל הקבוצות שנבחרו כבר רשומות למשימה",
        );
        setSeverity("warning");
        return;
      }

      setAlertText(
        addedCount === 1
          ? "הקבוצה נוספה למשימה בהצלחה"
          : `${addedCount} קבוצות נוספו למשימה בהצלחה`,
      );
      setSeverity("success");
    },
    onSuccess: (_data, variables) => {
      void invalidateEventParticipantQueries(
        queryClient,
        variables.opportunityId,
      );
    },
    onError: (error) => {
      if (error instanceof MatchWeightPromptCancelled) return;
      console.error(error.message);
      setAlertText(adminParticipantActionErrorMessage(error));
      setSeverity("error");
    },
  });

  return mutation;
}

export const useUpdateEventParticipant = () => {
  const { setAlertText, setSeverity } = useAlert();
  const { currentUser } = useUser();
  const queryClient = useQueryClient();

  const { data: updatedParticipant, ...mutation } = useMutation({
    mutationKey: ["updateEventParticipant", currentUser?.id],
    mutationFn: async ({
      opportunityParticipantId,
      showSuccessAlert = true,
    }: {
      opportunityParticipantId: string;
      opportunityId?: string;
      showSuccessAlert?: boolean;
    }) => {
      await opportunityParticipantRepo.update(opportunityParticipantId, {
        status: "DECLINED",
      });
      if (showSuccessAlert) {
        setAlertText("המשתתף הוסר בהצלחה");
        setSeverity("success");
      }
    },
    onSuccess: (_data, variables) => {
      void invalidateEventParticipantQueries(
        queryClient,
        variables.opportunityId,
      );
    },
    onError: (error) => {
      console.error(error.message);
    },
  });

  return {
    updatedParticipant,
    ...mutation,
  };
};

/* eslint-enable no-console -- error handling*/
